import { LineType } from '@insurgrid/common';
import { useState, useEffect } from 'react';
import { useParams } from 'next/navigation';

import { FORM_INSURANCE_LINE_KEY } from '../../components/views/FormView/sessionKeys';
import { useAllLinesMode } from './useAllLinesMode';
import { useMergedCarrierList } from './useMergedCarrierList';
import { lineForCarrier } from './carrierLines';

/** Resolve the line of business for the current consumer session.
 *
 *  Priority:
 *    0. All-lines link (`?lines=all`, agency flag on) on a carrier route —
 *       the line of the carrier the consumer picked from the merged list.
 *    1. `sessionStorage[FORM_INSURANCE_LINE_KEY]` — set by FormView's
 *       carrier handoff when the consumer came in through a Form Builder
 *       form. Agent's pick in the sidebar wins over the subdomain.
 *    2. URL subdomain (`commercial.*` → commercial, else personal) —
 *       legacy default for custom-link / standard-link consumers who
 *       didn't come through a Form Builder form. */
export function useLineType() {
  const params = useParams();
  const carrierSlug = params?.carrierSlug as string | undefined;
  const { allLines } = useAllLinesMode();
  const { carrierList: mergedCarriers } = useMergedCarrierList(allLines);
  const [subdomain, setSubdomain] = useState<string>('');
  const [formOverride, setFormOverride] = useState<LineType | null>(null);

  useEffect(() => {
    setSubdomain(window.location.host.split('.')[0]);
    try {
      const stored = window.sessionStorage.getItem(FORM_INSURANCE_LINE_KEY);
      if (stored === 'commercial') setFormOverride(LineType.Commercial);
      else if (stored === 'personal') setFormOverride(LineType.Personal);
    } catch {
      // sessionStorage can throw under strict privacy modes; fall back to subdomain.
    }
  }, []);

  if (allLines && carrierSlug) {
    return { lineType: lineForCarrier(mergedCarriers, carrierSlug) };
  }

  return {
    lineType:
      formOverride ??
      (subdomain.includes('commercial')
        ? LineType.Commercial
        : LineType.Personal),
  };
}
